import {
  clearCurrentUser,
  fetchCurrentUser,
  guestWishlistUpdated,
  resetNotificationFetchTimestamp,
} from './user.duck';
import { createUserWithIdp, checkUsernameAvailability, signupUser } from '../util/api';
import { storableError } from '../util/errors';
import * as log from '../util/log';
import { getAllCartItems } from '../containers/CartPage/cartHelpers';
import { queryListingsByIds } from '../util/listingsByIds';
import { denormalisedResponseEntities } from '../util/data';
import { isOwnCartListing } from '../util/cartRules';
import {
  getLocalCartItems,
  removeLocalCartItems,
  getLocalCartQuantities,
  removeLocalCartQuantities,
  getLocalWishlist,
  removeLocalWishlist,
} from '../util/guestStorage';
import { updateProfile } from '../containers/ProfileSettingsPage/ProfileSettingsPage.duck';

const authenticated = authInfo => authInfo && authInfo.isAnonymous === false;

// ================ Action types ================ //

export const AUTH_INFO_REQUEST = 'app/auth/AUTH_INFO_REQUEST';
export const AUTH_INFO_SUCCESS = 'app/auth/AUTH_INFO_SUCCESS';

export const LOGIN_REQUEST = 'app/auth/LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'app/auth/LOGIN_SUCCESS';
export const LOGIN_ERROR = 'app/auth/LOGIN_ERROR';

export const LOGOUT_REQUEST = 'app/auth/LOGOUT_REQUEST';
export const LOGOUT_SUCCESS = 'app/auth/LOGOUT_SUCCESS';
export const LOGOUT_ERROR = 'app/auth/LOGOUT_ERROR';

export const SIGNUP_REQUEST = 'app/auth/SIGNUP_REQUEST';
export const SIGNUP_SUCCESS = 'app/auth/SIGNUP_SUCCESS';
export const SIGNUP_ERROR = 'app/auth/SIGNUP_ERROR';

export const CONFIRM_REQUEST = 'app/auth/CONFIRM_REQUEST';
export const CONFIRM_SUCCESS = 'app/auth/CONFIRM_SUCCESS';
export const CONFIRM_ERROR = 'app/auth/CONFIRM_ERROR';

export const CHECK_USERNAME_REQUEST = 'app/auth/CHECK_USERNAME_REQUEST';
export const CHECK_USERNAME_SUCCESS = 'app/auth/CHECK_USERNAME_SUCCESS';
export const CHECK_USERNAME_ERROR = 'app/auth/CHECK_USERNAME_ERROR';
export const CLEAR_USERNAME_CHECK = 'app/auth/CLEAR_USERNAME_CHECK';

// Generic user_logout action that can be handled elsewhere
// E.g. src/reducers.js clears store as a consequence
export const USER_LOGOUT = 'app/USER_LOGOUT';

// ================ Reducer ================ //

const initialState = {
  isAuthenticated: false,

  // scopes associated with current token
  authScopes: [],

  // auth info
  authInfoLoaded: false,

  // login
  loginError: null,
  loginInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,

  // confirm (create use with idp)
  confirmError: null,
  confirmInProgress: false,

  // username check: the latest check asked for (an older answer arriving after it is dropped)
  usernameCheckInProgress: false,
  usernameCheckError: null,
  usernameAvailable: null,
  checkedUsername: null,
  usernameCheckRequest: 0,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case AUTH_INFO_REQUEST:
      return state;
    case AUTH_INFO_SUCCESS:
      return {
        ...state,
        authInfoLoaded: true,
        isAuthenticated: authenticated(payload),
        authScopes: payload.scopes,
      };

    case LOGIN_REQUEST:
      return {
        ...state,
        loginInProgress: true,
        loginError: null,
        logoutError: null,
        signupError: null,
      };
    case LOGIN_SUCCESS:
      return { ...state, loginInProgress: false, isAuthenticated: true };
    case LOGIN_ERROR:
      return { ...state, loginInProgress: false, loginError: payload };

    case LOGOUT_REQUEST:
      return { ...state, logoutInProgress: true, loginError: null, logoutError: null };
    case LOGOUT_SUCCESS:
      return { ...state, logoutInProgress: false, isAuthenticated: false, authScopes: [] };
    case LOGOUT_ERROR:
      return { ...state, logoutInProgress: false, logoutError: payload };

    case SIGNUP_REQUEST:
      return { ...state, signupInProgress: true, loginError: null, signupError: null };
    case SIGNUP_SUCCESS:
      return { ...state, signupInProgress: false };
    case SIGNUP_ERROR:
      return { ...state, signupInProgress: false, signupError: payload };

    case CONFIRM_REQUEST:
      return { ...state, confirmInProgress: true, loginError: null, confirmError: null };
    case CONFIRM_SUCCESS:
      return { ...state, confirmInProgress: false, isAuthenticated: true };
    case CONFIRM_ERROR:
      return { ...state, confirmInProgress: false, confirmError: payload };

    case CHECK_USERNAME_REQUEST:
      return {
        ...state,
        usernameCheckInProgress: true,
        usernameCheckError: null,
        usernameAvailable: null,
        checkedUsername: payload,
        usernameCheckRequest: action.meta.request,
      };
    case CHECK_USERNAME_SUCCESS:
      return action.meta?.request !== state.usernameCheckRequest
        ? state
        : {
            ...state,
            usernameCheckInProgress: false,
            usernameAvailable: payload.available,
            checkedUsername: payload.username,
          };
    case CHECK_USERNAME_ERROR:
      return action.meta?.request !== state.usernameCheckRequest
        ? state
        : {
            ...state,
            usernameCheckInProgress: false,
            usernameCheckError: payload,
            usernameAvailable: null,
          };
    case CLEAR_USERNAME_CHECK:
      return {
        ...state,
        usernameCheckInProgress: false,
        usernameCheckError: null,
        usernameAvailable: null,
        checkedUsername: null,
        usernameCheckRequest: action.meta?.request ?? state.usernameCheckRequest,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const authenticationInProgress = state => {
  const { loginInProgress, logoutInProgress, signupInProgress } = state.auth;
  return loginInProgress || logoutInProgress || signupInProgress;
};

export const selectIsAuthenticated = state => state.auth.isAuthenticated;

// ================ Action creators ================ //

export const authInfoRequest = () => ({ type: AUTH_INFO_REQUEST });
export const authInfoSuccess = info => ({ type: AUTH_INFO_SUCCESS, payload: info });

export const loginRequest = () => ({ type: LOGIN_REQUEST });
export const loginSuccess = () => ({ type: LOGIN_SUCCESS });
export const loginError = error => ({ type: LOGIN_ERROR, payload: error, error: true });

export const logoutRequest = () => ({ type: LOGOUT_REQUEST });
export const logoutSuccess = () => ({ type: LOGOUT_SUCCESS });
export const logoutError = error => ({ type: LOGOUT_ERROR, payload: error, error: true });

export const signupRequest = () => ({ type: SIGNUP_REQUEST });
export const signupSuccess = () => ({ type: SIGNUP_SUCCESS });
export const signupError = error => ({ type: SIGNUP_ERROR, payload: error, error: true });

export const confirmRequest = () => ({ type: CONFIRM_REQUEST });
export const confirmSuccess = () => ({ type: CONFIRM_SUCCESS });
export const confirmError = error => ({ type: CONFIRM_ERROR, payload: error, error: true });

export const checkUsernameRequest = (username, request) => ({
  type: CHECK_USERNAME_REQUEST,
  payload: username,
  meta: { request },
});
export const checkUsernameSuccess = (data, request) => ({
  type: CHECK_USERNAME_SUCCESS,
  payload: data,
  meta: { request },
});
export const checkUsernameError = (error, request) => ({
  type: CHECK_USERNAME_ERROR,
  payload: error,
  error: true,
  meta: { request },
});
export const clearUsernameCheck = request => ({ type: CLEAR_USERNAME_CHECK, meta: { request } });

export const userLogout = () => ({ type: USER_LOGOUT });

// ================ Thunks ================ //

export const authInfo = () => (dispatch, getState, sdk) => {
  dispatch(authInfoRequest());
  return sdk
    .authInfo()
    .then(info => dispatch(authInfoSuccess(info)))
    .catch(e => {
      // Requesting auth info just reads the token from the token
      // store (i.e. cookies), and should not fail in normal
      // circumstances. If it fails, it's due to a programming
      // error. In that case we mark the operation done and dispatch
      // `null` success action that marks the user as unauthenticated.
      log.error(e, 'auth-info-failed');
      dispatch(authInfoSuccess(null));
    });
};
// The guest cart's listings that are the buyer's own: a seller who added their own
// listing while signed out can't buy it, so it stays out of their account's cart.
// When the listings can't be read, none are left out (the cart page leaves them out too).
const ownListingIds = (sdk, ids, currentUserId) =>
  Promise.resolve()
    .then(() =>
      queryListingsByIds(sdk, ids, {
        include: ['author'],
        'fields.listing': ['title'],
        'fields.user': ['profile.displayName'],
      })
    )
    .then(response =>
      denormalisedResponseEntities(response)
        .filter(listing => isOwnCartListing(listing, currentUserId))
        .map(listing => listing.id.uuid)
    )
    .catch(() => []);

const mergeGuestData = async (currentUser, dispatch, sdk) => {
  const localCartItems = getLocalCartItems();
  const localWishlist = getLocalWishlist();
  const hasLocalCart = localCartItems.length > 0;
  const hasLocalWishlist = localWishlist.length > 0;

  if (!currentUser?.id || (!hasLocalCart && !hasLocalWishlist)) {
    return null;
  }

  const profile = currentUser.attributes?.profile || {};
  const mergedWishlist = Array.from(
    new Set([...(profile.publicData?.wishlist || []), ...localWishlist])
  );
  const own = hasLocalCart ? await ownListingIds(sdk, localCartItems, currentUser.id.uuid) : [];
  const localQuantities = Object.fromEntries(
    Object.entries(getLocalCartQuantities()).filter(([id]) => !own.includes(id))
  );

  const payload = {
    ...(hasLocalCart
      ? {
          protectedData: {
            cartItems: getAllCartItems(currentUser).filter(id => !own.includes(id)),
            cartQuantities: {
              ...(profile.protectedData?.cartQuantities || {}),
              ...localQuantities,
            },
          },
        }
      : {}),
    ...(hasLocalWishlist ? { publicData: { wishlist: mergedWishlist } } : {}),
  };

  try {
    // updateProfile never rejects: it resolves with the updated user entity
    // on success and with the dispatched error action on failure.
    const result = await dispatch(updateProfile(payload));
    if (!result?.id) {
      log.error(new Error('updateProfile did not return a user'), 'merge-guest-data-failed');
      return null;
    }
    if (hasLocalCart) {
      removeLocalCartItems();
      removeLocalCartQuantities();
    }
    if (hasLocalWishlist) {
      removeLocalWishlist();
      dispatch(guestWishlistUpdated([]));
    }
  } catch (e) {
    log.error(storableError(e), 'merge-guest-data-failed');
  }
  return null;
};

// Merge the guest (localStorage) cart and wishlist into the account profile.
// Dispatched from fetchCurrentUser, which waits for it, so every auth flavor is
// covered: email login, email signup, first-time IdP signup, and returning social
// logins (which re-enter through the app bootstrap without any auth thunk).
// Idempotent: a no-op when localStorage is empty, and localStorage is only
// cleared after the profile update succeeds, so a failed update keeps the
// guest data for the next attempt instead of silently losing it.
// One merge at a time: a second call while one runs waits for that one instead
// of saving the same guest data again. Never rejects.
let mergeInProgress = null;
export const mergeGuestDataIntoAccount = currentUser => (dispatch, getState, sdk) => {
  // No guest data on the server, and requests there mustn't share one merge
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (!mergeInProgress) {
    mergeInProgress = mergeGuestData(currentUser, dispatch, sdk)
      .catch(e => {
        log.error(storableError(e), 'merge-guest-data-failed');
        return null;
      })
      .finally(() => {
        mergeInProgress = null;
      });
  }
  return mergeInProgress;
};

export const login = (username, password) => async (dispatch, getState, sdk) => {
  // Check if authentication is already in progress
  if (authenticationInProgress(getState())) {
    throw new Error('Login or logout already in progress');
  }

  // Dispatch login request action
  dispatch(loginRequest());

  try {
    // Perform login
    await sdk.login({ username, password });

    // Dispatch login success action
    dispatch(loginSuccess());

    // Fetch current user (guest cart/wishlist merge happens there)
    await dispatch(fetchCurrentUser());
  } catch (error) {
    // Dispatch login error action
    dispatch(loginError(storableError(error)));
  }
};

export const logout = () => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(logoutRequest());

  // Note that the thunk does not reject when the logout fails, it
  // just dispatches the logout error action.
  return sdk
    .logout()
    .then(() => {
      // The order of the dispatched actions
      dispatch(logoutSuccess());
      dispatch(clearCurrentUser());
      resetNotificationFetchTimestamp(); // Reset notification fetch timestamp on logout
      log.clearUserId();
      dispatch(userLogout());
    })
    .catch(e => dispatch(logoutError(storableError(e))));
};

export const signup = params => async (dispatch, getState) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(signupRequest());

  // Signup is proxied through our server for honeypot, rate limiting,
  // and OOPSpam checks before the Sharetribe account is created.
  // The server handles both create and login, so we dispatch loginSuccess
  // to update Redux auth state, then fetch the current user (and merge cart).
  try {
    await signupUser(params);
    dispatch(signupSuccess());
    dispatch(loginSuccess());

    // Fetch current user (guest cart/wishlist merge happens there)
    await dispatch(fetchCurrentUser());
  } catch (e) {
    dispatch(signupError(storableError(e)));
    log.error(e, 'signup-failed', { email: params.email });
  }
};

export const signupWithIdp = params => (dispatch, getState, sdk) => {
  dispatch(confirmRequest());
  return createUserWithIdp(params)
    .then(res => {
      return dispatch(confirmSuccess());
    })
    .then(() => dispatch(fetchCurrentUser()))
    .catch(e => {
      log.error(e, 'create-user-with-idp-failed', { params });
      return dispatch(confirmError(storableError(e)));
    });
};

// The availability check waits for typing to pause this long: one request per pause, not
// one per keystroke.
export const USERNAME_CHECK_DELAY_MS = 300;
let usernameCheckRequests = 0;
let usernameCheckTimer = null;
let answerSupersededCheck = null;

/**
 * Is this username free? Resolves true (free) or false (taken) once typing pauses, or null
 * when a newer check replaced this one before it was sent (the newer one answers). A check
 * that fails rejects: it says nothing about the username.
 */
export const checkUsername = username => dispatch => {
  usernameCheckRequests += 1;
  const request = usernameCheckRequests;
  clearTimeout(usernameCheckTimer);
  if (answerSupersededCheck) {
    answerSupersededCheck(null);
    answerSupersededCheck = null;
  }

  // Too short to check: the forms say so themselves
  if (!username || username.length < 3) {
    dispatch(clearUsernameCheck(request));
    return Promise.resolve(true);
  }

  dispatch(checkUsernameRequest(username, request));
  return new Promise((resolve, reject) => {
    answerSupersededCheck = resolve;
    usernameCheckTimer = setTimeout(() => {
      answerSupersededCheck = null;
      checkUsernameAvailability(username)
        .then(response => {
          const { data } = response;
          dispatch(
            checkUsernameSuccess({ username: data.username, available: data.available }, request)
          );
          resolve(data.available);
        })
        .catch(error => {
          dispatch(checkUsernameError(storableError(error), request));
          log.error(error, 'check-username-failed', { username });
          reject(error);
        });
    }, USERNAME_CHECK_DELAY_MS);
  });
};
